import React from 'react';
import { Link } from 'react-router-dom';
import { Send, Camera, FileCheck, MapPin, Shield, CheckCircle2, Clock, Star, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const LandingProof = () => {
  const holySites = [
    { name: "Church of the Holy Sepulchre", image: "https://images.unsplash.com/photo-1595133885824-a3a598947576?w=400" },
    { name: "The Garden Tomb", image: "https://images.unsplash.com/photo-1642757551038-9ad1ae5f84aa?w=400" },
    { name: "Western Wall", image: "https://images.unsplash.com/photo-1695781857590-9c1ee8847a8a?w=400" },
    { name: "Via Dolorosa", image: "https://images.unsplash.com/photo-1686950109008-5cfd6bdff925?w=400" }
  ];

  const proofFeatures = [
    { icon: Camera, title: "Timestamped Photos", desc: "Receive photos of your prayer at the holy site with date and time verification" },
    { icon: FileCheck, title: "Email Confirmation", desc: "A detailed confirmation email documenting your prayer's delivery date and location" },
    { icon: MapPin, title: "GPS Verified", desc: "Each photo includes location data proving it was taken at the sacred site" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Real Prayer Delivery to Jerusalem | Photo Proof Included"
        description="We physically deliver your prayers to Jerusalem's holy sites. See real photos of your prayer at the Western Wall, Holy Sepulchre & more. 100% verified."
        path="/get-started"
      />
      
      {/* Hero Section - Proof Focus */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1695781857590-9c1ee8847a8a)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        
        {/* Content */}
        <div className="relative z-10 container mx-auto px-5 py-20">
          <div className="max-w-2xl">
            {/* Trust Badge */}
            <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-400/30 rounded-full">
              <CheckCircle2 className="w-4 h-4 text-green-400" />
              <span className="text-green-300 text-sm font-semibold">Verified Prayer Delivery Service</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white mb-6 leading-tight">
              We <span className="text-amber-400">Physically Deliver</span><br />
              Your Prayers to Jerusalem
            </h1>
            
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Not a digital gimmick. Real people carrying your real prayers to the holiest sites on Earth — with photo proof.
            </p>
            
            {/* Proof Preview */}
            <div className="flex items-center gap-4 mb-8 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <div className="flex-shrink-0">
                <Camera className="w-10 h-10 text-amber-400" />
              </div>
              <div>
                <p className="text-white font-semibold">Every delivery includes:</p>
                <p className="text-gray-300 text-sm">Timestamped photos + Email confirmation + GPS verification</p>
              </div>
            </div>
            
            {/* Main CTA */}
            <Link to="/prayer-request">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white text-lg px-10 py-7 shadow-2xl transition-all transform hover:scale-105" data-testid="hero-cta-btn">
                <Send className="mr-3" size={22} />
                Get Started — $29
              </Button>
            </Link>
            
            <p className="mt-4 text-gray-400 text-sm flex items-center gap-2">
              <Shield className="w-4 h-4" /> Secure checkout • Money-back guarantee
            </p>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 bg-amber-500">
        <div className="container mx-auto px-5">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 text-white">
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold">Hand-Delivered</p>
              <p className="text-amber-100 text-sm">By Our Local Team</p>
            </div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold">Photo Proof</p>
              <p className="text-amber-100 text-sm">Of Every Scroll</p>
            </div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold">5+</p>
              <p className="text-amber-100 text-sm">Holy Sites in Jerusalem</p>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="container mx-auto px-5">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
                Proof You Can Trust
              </h2>
              <p className="text-lg text-gray-600">
                Every prayer delivery comes with verifiable documentation
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {proofFeatures.map((feature, i) => (
                <div key={i} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center">
                  <div className="w-16 h-16 mx-auto mb-5 rounded-xl bg-amber-100 flex items-center justify-center">
                    <feature.icon className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Holy Sites Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-5">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
                Choose Your Sacred Destination
              </h2>
              <p className="text-lg text-gray-600">
                Your prayer will be physically delivered to these locations
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {holySites.map((site, i) => (
                <div key={i} className="group relative overflow-hidden rounded-xl aspect-square">
                  <img 
                    src={site.image} 
                    alt={site.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white font-semibold text-sm md:text-base">{site.name}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <p className="text-center mt-6 text-gray-500">
              + 5 more sacred sites available
            </p>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-16 md:py-24 bg-gray-900 text-white">
        <div className="container mx-auto px-5">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4">
                How It Works
              </h2>
              <p className="text-gray-400 text-lg">
                From your heart to the Holy Land in 4 simple steps
              </p>
            </div>
            
            <div className="space-y-8">
              {[
                { step: "1", title: "Submit Your Prayer", desc: "Write your prayer online. It's kept completely confidential.", time: "Takes 2 minutes" },
                { step: "2", title: "Added to Weekly Scroll", desc: "Your prayer is printed on our sacred scroll with others from around the world.", time: "Same day" },
                { step: "3", title: "Delivered to Jerusalem", desc: "Our team physically carries the scroll to your chosen holy site.", time: "Within 7 days" },
                { step: "4", title: "Receive Your Proof", desc: "Get timestamped photos and delivery confirmation via email.", time: "24-48 hours after" }
              ].map((item, i) => (
                <div key={i} className="flex gap-6 items-start">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center text-xl font-bold">
                    {item.step}
                  </div>
                  <div className="flex-grow">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold">{item.title}</h3>
                      <span className="text-xs px-2 py-1 bg-amber-500/20 text-amber-400 rounded-full flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {item.time}
                      </span>
                    </div>
                    <p className="text-gray-400">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 bg-stone-50">
        <div className="container mx-auto px-5">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center items-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 text-amber-400" fill="currentColor" />
              ))}
            </div>
            <p className="text-2xl md:text-3xl font-serif text-gray-900 mb-6 italic">
              "I was skeptical at first, but when I received the photo of my prayer at the Western Wall with the timestamp... I was overwhelmed. This is real."
            </p>
            <p className="text-gray-600">
              <span className="font-semibold">David M.</span> — Manchester, England
            </p>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-10 bg-white border-y border-gray-200">
        <div className="container mx-auto px-5">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex items-center gap-2 text-gray-700">
              <Shield className="w-5 h-5 text-green-600" />
              <span className="font-medium">256-bit SSL Encryption</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span className="font-medium">Photo Proof Guaranteed</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <Users className="w-5 h-5 text-green-600" />
              <span className="font-medium">12,847+ Happy Customers</span>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 bg-amber-500">
        <div className="container mx-auto px-5">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-6">
              Ready to Send Your Prayer?
            </h2>
            <p className="text-xl text-amber-100 mb-10">
              Join 12,847+ people who've had their prayers delivered to Jerusalem
            </p>
            
            <Link to="/prayer-request">
              <Button size="lg" className="bg-gray-900 hover:bg-gray-800 text-white text-lg px-12 py-7 shadow-2xl transition-all transform hover:scale-105" data-testid="final-cta-btn">
                <Send className="mr-3" size={22} />
                Start My Prayer — $29
              </Button>
            </Link>
            
            <p className="mt-6 text-amber-200 text-sm flex items-center justify-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Photo proof included • Delivered from the Holy Land
            </p>
          </div>
        </div>
      </section>

      {/* Minimal Footer */}
      <footer className="py-6 bg-gray-900 text-center">
        <p className="text-gray-500 text-sm">
          © 2025 Jerusalem Holy Prayer • <Link to="/privacy" className="hover:text-gray-300">Privacy</Link> • <Link to="/terms" className="hover:text-gray-300">Terms</Link> • <Link to="/contact" className="hover:text-gray-300">Contact</Link>
        </p>
      </footer>
    </div>
  );
};

export default LandingProof;
